import { useEffect, useMemo, useState } from 'react';
import { startOfDay, subMonths } from 'date-fns';
import { isEqual } from 'lodash-es';
import { FilterPicker, RadioButtonWithLabel, KeyboardDatePicker } from 'seagull';
import { useIoContext } from "../../contexts";
import { relationshipSummaryModel, timeModel } from "../../models";
import { SearchFilterType } from "../Reports/components/types";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export let FilterType = /*#__PURE__*/function (FilterType) {
  FilterType["AgreementStatus"] = "agreementStatus";
  FilterType["EffectiveDate"] = "effectiveDate";
  FilterType["LastBillingDate"] = "lastBillingDate";
  FilterType["PaymentMethod"] = "paymentMethod";
  FilterType["HasCredit"] = "hasCredit";
  FilterType["Completion"] = "completion";
  FilterType["BillingTrigger"] = "billingTrigger";
  FilterType["Recurrence"] = "recurrence";
  FilterType["Automatic"] = "automatic";
  FilterType["ServiceNames"] = "serviceNames";
  FilterType["ServicePriceIncreaseState"] = "servicePriceIncreaseState";
  FilterType["HasAmendments"] = "hasAmendments";
  return FilterType;
}({});
export let PriceIncreaseFilterValue = /*#__PURE__*/function (PriceIncreaseFilterValue) {
  PriceIncreaseFilterValue["None"] = "none";
  PriceIncreaseFilterValue["Automatic"] = "automatic";
  return PriceIncreaseFilterValue;
}({});
export const defaultAgreementStatusFilter = [relationshipSummaryModel.AgreementStatus.Active, relationshipSummaryModel.AgreementStatus.Exhausted];
export const tableFilterLabels = {
  [FilterType.AgreementStatus]: 'Agreement status',
  [FilterType.EffectiveDate]: 'Effective date',
  [FilterType.LastBillingDate]: 'Last billing date',
  [FilterType.PaymentMethod]: 'Payment method',
  [FilterType.HasCredit]: 'Has credit',
  [FilterType.Completion]: 'Service status',
  [FilterType.Recurrence]: 'Occurrence',
  [FilterType.Automatic]: 'Automatic services state',
  [FilterType.BillingTrigger]: 'Billing trigger',
  [FilterType.ServiceNames]: 'Service name',
  [FilterType.ServicePriceIncreaseState]: 'Automatic price increase',
  [FilterType.HasAmendments]: 'Is amended'
};
export const tableFilterGroups = [{
  label: 'Agreements',
  filters: [FilterType.AgreementStatus, FilterType.EffectiveDate, FilterType.LastBillingDate, FilterType.PaymentMethod, FilterType.HasCredit, FilterType.ServicePriceIncreaseState, FilterType.HasAmendments]
}, {
  label: 'Services',
  filters: [FilterType.ServiceNames, FilterType.Completion, FilterType.BillingTrigger, FilterType.Recurrence, FilterType.Automatic]
}];
export function deserializeFiltersFromURL(urlParams) {
  return {
    [FilterType.AgreementStatus]: deserializeFilter(FilterType.AgreementStatus, urlParams.agreementStatus),
    [FilterType.EffectiveDate]: deserializeFilter(FilterType.EffectiveDate, urlParams.effectiveDate),
    [FilterType.LastBillingDate]: deserializeFilter(FilterType.LastBillingDate, urlParams.lastBillingDate),
    [FilterType.PaymentMethod]: deserializeFilter(FilterType.PaymentMethod, urlParams.paymentMethod),
    [FilterType.Completion]: deserializeFilter(FilterType.Completion, urlParams.completion),
    [FilterType.Recurrence]: deserializeFilter(FilterType.Recurrence, urlParams.recurrence),
    [FilterType.HasCredit]: deserializeFilter(FilterType.HasCredit, urlParams.hasCredit),
    [FilterType.Automatic]: deserializeFilter(FilterType.Automatic, urlParams.automatic),
    [FilterType.BillingTrigger]: deserializeFilter(FilterType.BillingTrigger, urlParams.billingTrigger),
    [FilterType.ServiceNames]: deserializeFilter(FilterType.ServiceNames, urlParams.serviceNames),
    [FilterType.ServicePriceIncreaseState]: deserializeFilter(FilterType.ServicePriceIncreaseState, urlParams.servicePriceIncreaseState),
    [FilterType.HasAmendments]: deserializeFilter(FilterType.HasAmendments, urlParams.hasAmendments)
  };
  function deserializeFilter(type, value) {
    const {
      deserialize
    } = filtersConfig[type];
    if (typeof value !== 'string') return value;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return deserialize ? deserialize(value) : value;
  }
}
const effectiveDateLabel = 'Effective date';
const lastBillingDateLabel = 'Last billing date';
const agreementStatusFilterOptions = [{
  value: relationshipSummaryModel.AgreementStatus.Active,
  label: 'Active'
}, {
  value: relationshipSummaryModel.AgreementStatus.Exhausted,
  label: 'Completed'
}, {
  value: relationshipSummaryModel.AgreementStatus.Terminated,
  label: 'Terminated'
}];
export let HasAmendmentsFilterValue = /*#__PURE__*/function (HasAmendmentsFilterValue) {
  HasAmendmentsFilterValue["True"] = "true";
  HasAmendmentsFilterValue["False"] = "false";
  return HasAmendmentsFilterValue;
}({});
const hasAmendmentsFilterOptions = [{
  value: HasAmendmentsFilterValue.True,
  label: 'Yes'
}, {
  value: HasAmendmentsFilterValue.False,
  label: 'No'
}];
export function EffectiveDateFilter(props) {
  const {
    value,
    startOpen,
    onChange,
    onRemove,
    onInputBlur,
    onInputFocus
  } = props;
  const typedValue = value !== null && value !== void 0 ? value : {};
  const {
    getNow
  } = useIoContext();
  const hasDatesFromProps = !!(typedValue.fromDate && typedValue.toDate);
  const [selectingRange, setSelectingRange] = useState(hasDatesFromProps);
  const [fromDate, setFromDate] = useState(getInitialFromDate);
  const [toDate, setToDate] = useState(getInitialToDate);
  useEffect(() => {
    typedValue.fromDate && setFromDate(typedValue.fromDate);
  }, [typedValue.fromDate]);
  useEffect(() => {
    typedValue.toDate && setToDate(typedValue.toDate);
  }, [typedValue.toDate]);
  const label = useMemo(() => {
    if (typedValue.fromDate && typedValue.toDate) {
      return "".concat(effectiveDateLabel, " (").concat(timeModel.formatDate(typedValue.fromDate), "-").concat(timeModel.formatDate(typedValue.toDate), ")");
    }
    return effectiveDateLabel;
  }, [typedValue.fromDate, typedValue.toDate]);
  function onChangeIfDifferentValue(newValue) {
    if (!isEqual(newValue, typedValue)) {
      onChange(newValue);
    }
  }
  function getInitialFromDate() {
    return typedValue.fromDate || subMonths(getNow(), 1);
  }
  function getInitialToDate() {
    return typedValue.toDate || getNow();
  }
  return /*#__PURE__*/_jsxs(FilterPicker, {
    removable: true,
    startOpen: startOpen,
    testId: "effective-date-filter",
    disableApply: selectingRange && fromDate && toDate && fromDate.getTime() > toDate.getTime(),
    label: label,
    title: effectiveDateLabel,
    highlightTrigger: !!(typedValue.fromDate && typedValue.toDate),
    onRemove: onRemove,
    onApply: () => onChangeIfDifferentValue(selectingRange ? {
      fromDate,
      toDate
    } : {}),
    onClose: opts => {
      if (opts !== null && opts !== void 0 && opts.revert) {
        setSelectingRange(hasDatesFromProps);
        setFromDate(getInitialFromDate());
        setToDate(getInitialToDate());
      }
    },
    children: [/*#__PURE__*/_jsx(RadioButtonWithLabel, {
      label: "Select all",
      testId: "effective-date-filter-all",
      checked: !selectingRange,
      onChange: () => setSelectingRange(false)
    }), /*#__PURE__*/_jsx(RadioButtonWithLabel, {
      label: "Specific range",
      testId: "effective-date-filter-range",
      checked: selectingRange,
      onChange: () => {
        setSelectingRange(true);
        const now = startOfDay(getNow());
        if (!fromDate) {
          setFromDate(now);
        }
        if (!toDate) {
          setToDate(now);
        }
      }
    }), selectingRange && fromDate && toDate && /*#__PURE__*/_jsx(DateRangePicker, {
      testId: "effective-date-filter",
      fromDate: fromDate,
      toDate: toDate,
      onChange: data => {
        setFromDate(data.fromDate);
        setToDate(data.toDate);
      },
      onInputFocus: onInputFocus,
      onInputBlur: onInputBlur
    })]
  });
}
export let LastBillingDateType = /*#__PURE__*/function (LastBillingDateType) {
  LastBillingDateType["None"] = "none";
  LastBillingDateType["Never"] = "never";
  LastBillingDateType["Ever"] = "ever";
  LastBillingDateType["Range"] = "range";
  return LastBillingDateType;
}({});
function getLastBillingDateOptionLabel(type) {
  switch (type) {
    case LastBillingDateType.Ever:
      return 'Ever billed';
    case LastBillingDateType.Never:
      return 'Never billed';
    case LastBillingDateType.Range:
      return 'Specific range';
    case LastBillingDateType.None:
      return '';
  }
}
export function LastBillingDateFilter(props) {
  const {
    value,
    startOpen,
    onChange,
    onRemove,
    onInputBlur,
    onInputFocus
  } = props;
  const typedValue = value !== null && value !== void 0 ? value : {
    type: LastBillingDateType.None
  };
  const {
    getNow
  } = useIoContext();
  const [type, setType] = useState(typedValue.type);
  const [fromDate, setFromDate] = useState(getInitialFromDate);
  const [toDate, setToDate] = useState(getInitialToDate);
  const disableApply = fromDate && toDate && fromDate.getTime() > toDate.getTime();
  useEffect(() => {
    setType(typedValue.type);
  }, [typedValue.type]);
  useEffect(() => {
    typedValue.fromDate && setFromDate(typedValue.fromDate);
  }, [typedValue.fromDate]);
  useEffect(() => {
    typedValue.toDate && setToDate(typedValue.toDate);
  }, [typedValue.toDate]);
  const label = useMemo(() => {
    switch (typedValue.type) {
      case LastBillingDateType.Ever:
      case LastBillingDateType.Never:
        return "".concat(lastBillingDateLabel, " (").concat(getLastBillingDateOptionLabel(typedValue.type), ")");
      case LastBillingDateType.Range:
        return "".concat(lastBillingDateLabel, " (").concat(timeModel.formatDate(typedValue.fromDate), "-").concat(timeModel.formatDate(typedValue.toDate), ")");
      case LastBillingDateType.None:
        return lastBillingDateLabel;
    }
  }, [typedValue]);
  function onChangeIfDifferentValue(newValue) {
    if (!isEqual(newValue, typedValue)) {
      onChange(newValue);
    }
  }
  function getInitialFromDate() {
    return typedValue.fromDate || subMonths(getNow(), 1);
  }
  function getInitialToDate() {
    return typedValue.toDate || getNow();
  }
  return /*#__PURE__*/_jsxs(FilterPicker, {
    removable: true,
    startOpen: startOpen,
    testId: "last-billing-date-filter",
    disableApply: disableApply,
    label: label,
    title: lastBillingDateLabel,
    highlightTrigger: typedValue.type !== LastBillingDateType.None,
    onRemove: onRemove,
    onApply: () => {
      if (type === LastBillingDateType.Range) {
        fromDate && toDate && onChangeIfDifferentValue({
          type,
          fromDate,
          toDate
        });
      } else {
        onChangeIfDifferentValue({
          type
        });
      }
    },
    onClose: opts => {
      if (opts !== null && opts !== void 0 && opts.revert) {
        setType(value.type);
        setFromDate(getInitialFromDate());
        setToDate(getInitialToDate());
      }
    },
    children: [/*#__PURE__*/_jsx(RadioButtonWithLabel, {
      label: "Select all",
      testId: "last-billing-date-filter-all",
      checked: type === LastBillingDateType.None,
      onChange: () => setType(LastBillingDateType.None)
    }), /*#__PURE__*/_jsx(RadioButtonWithLabel, {
      label: getLastBillingDateOptionLabel(LastBillingDateType.Ever),
      testId: "last-billing-date-filter-ever",
      checked: type === LastBillingDateType.Ever,
      onChange: () => setType(LastBillingDateType.Ever)
    }), /*#__PURE__*/_jsx(RadioButtonWithLabel, {
      label: getLastBillingDateOptionLabel(LastBillingDateType.Never),
      testId: "last-billing-date-filter-never",
      checked: type === LastBillingDateType.Never,
      onChange: () => setType(LastBillingDateType.Never)
    }), /*#__PURE__*/_jsx(RadioButtonWithLabel, {
      label: getLastBillingDateOptionLabel(LastBillingDateType.Range),
      testId: "last-billing-date-filter-range",
      checked: type === LastBillingDateType.Range,
      onChange: () => {
        setType(LastBillingDateType.Range);
        const now = startOfDay(getNow());
        if (!fromDate) {
          setFromDate(now);
        }
        if (!toDate) {
          setToDate(now);
        }
      }
    }), type === LastBillingDateType.Range && fromDate && toDate && /*#__PURE__*/_jsx(DateRangePicker, {
      testId: "last-billing-date-filter",
      fromDate: fromDate,
      toDate: toDate,
      onChange: data => {
        setFromDate(data.fromDate);
        setToDate(data.toDate);
      },
      onInputFocus: onInputFocus,
      onInputBlur: onInputBlur
    })]
  });
}
export let PaymentMethodFilterValue = /*#__PURE__*/function (PaymentMethodFilterValue) {
  PaymentMethodFilterValue["CreditCard"] = "credit";
  PaymentMethodFilterValue["BankAccount"] = "bank";
  PaymentMethodFilterValue["VerificationInitiated"] = "verificationInitiated";
  PaymentMethodFilterValue["VerificationFailed"] = "verificationFailed";
  PaymentMethodFilterValue["NotSet"] = "notSet";
  return PaymentMethodFilterValue;
}({});
const paymentMethodFilterOptions = [{
  value: PaymentMethodFilterValue.CreditCard,
  label: 'Credit card'
}, {
  value: PaymentMethodFilterValue.BankAccount,
  label: 'Bank account'
}, {
  value: PaymentMethodFilterValue.VerificationInitiated,
  label: 'Manual verification initiated'
}, {
  value: PaymentMethodFilterValue.VerificationFailed,
  label: 'Manual verification failed'
}, {
  value: PaymentMethodFilterValue.NotSet,
  label: 'Not set'
}];
export let CompletionFilterValue = /*#__PURE__*/function (CompletionFilterValue) {
  CompletionFilterValue["Completed"] = "completed";
  CompletionFilterValue["NotCompleted"] = "notCompleted";
  return CompletionFilterValue;
}({});
const completionFilterOptions = [{
  value: CompletionFilterValue.NotCompleted,
  label: 'Not completed'
}, {
  value: CompletionFilterValue.Completed,
  label: 'Completed'
}];
export let RecurrenceFilterValue = /*#__PURE__*/function (RecurrenceFilterValue) {
  RecurrenceFilterValue["OneTime"] = "oneTime";
  RecurrenceFilterValue["Weekly"] = "weekly";
  RecurrenceFilterValue["Biweekly"] = "biweekly";
  RecurrenceFilterValue["Monthly"] = "monthly";
  RecurrenceFilterValue["Quarterly"] = "quarterly";
  RecurrenceFilterValue["Yearly"] = "yearly";
  return RecurrenceFilterValue;
}({});
const recurrenceFilterOptions = [{
  value: RecurrenceFilterValue.OneTime,
  label: 'One time'
}, {
  value: RecurrenceFilterValue.Weekly,
  label: 'Weekly'
}, {
  value: RecurrenceFilterValue.Biweekly,
  label: 'Bi-weekly'
}, {
  value: RecurrenceFilterValue.Monthly,
  label: 'Monthly'
}, {
  value: RecurrenceFilterValue.Quarterly,
  label: 'Quarterly'
}, {
  value: RecurrenceFilterValue.Yearly,
  label: 'Yearly'
}];
export let HasCreditFilterValue = /*#__PURE__*/function (HasCreditFilterValue) {
  HasCreditFilterValue["Yes"] = "yes";
  HasCreditFilterValue["No"] = "no";
  return HasCreditFilterValue;
}({});
const hasCreditFilterOptions = [{
  value: HasCreditFilterValue.Yes,
  label: 'Yes'
}, {
  value: HasCreditFilterValue.No,
  label: 'No'
}];
export let AutomaticFilterValue = /*#__PURE__*/function (AutomaticFilterValue) {
  AutomaticFilterValue["Active"] = "active";
  AutomaticFilterValue["Paused"] = "paused";
  return AutomaticFilterValue;
}({});
const automaticFilterOptions = [{
  value: AutomaticFilterValue.Active,
  label: 'Active'
}, {
  value: AutomaticFilterValue.Paused,
  label: 'Paused'
}];
export let BillingTriggerFilterValue = /*#__PURE__*/function (BillingTriggerFilterValue) {
  BillingTriggerFilterValue["Automatic"] = "automatic";
  BillingTriggerFilterValue["Manual"] = "manual";
  return BillingTriggerFilterValue;
}({});
const billingTriggerFilterOptions = [{
  value: BillingTriggerFilterValue.Automatic,
  label: 'Automatic'
}, {
  value: BillingTriggerFilterValue.Manual,
  label: 'Manual'
}];
function DateRangePicker(props) {
  const {
    fromDate,
    toDate,
    testId,
    onChange: _onChange,
    onInputBlur,
    onInputFocus
  } = props;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(KeyboardDatePicker, {
      label: "From",
      testId: testId && "".concat(testId, "-from"),
      value: fromDate,
      maxDate: toDate,
      onChange: date => date && _onChange && _onChange({
        fromDate: date,
        toDate
      }),
      onFocus: onInputFocus,
      onBlur: onInputBlur
    }), /*#__PURE__*/_jsx(KeyboardDatePicker, {
      label: "To",
      testId: testId && "".concat(testId, "-to"),
      value: toDate,
      minDate: fromDate,
      onChange: date => date && _onChange && _onChange({
        fromDate,
        toDate: date
      }),
      onFocus: onInputFocus,
      onBlur: onInputBlur
    })]
  });
}
const priceIncreaseFilterOptions = [{
  value: PriceIncreaseFilterValue.Automatic,
  label: 'Included'
}, {
  value: PriceIncreaseFilterValue.None,
  label: 'Not included'
}];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSimpleMultiSelectFilterConfig(type) {
  return {
    hasValue(v) {
      return v.length > 0;
    },
    getEmptyValue() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return [];
    },
    getRequestParam(v) {
      return {
        [type]: v.length ? v.join(',') : undefined
      };
    }
  };
}
const effectiveDateConfig = {
  hasValue(effectiveDate) {
    return !!(effectiveDate.fromDate && effectiveDate.toDate);
  },
  getEmptyValue() {
    return {};
  },
  getRequestParam(effectiveDate) {
    return {
      fromDate: effectiveDate.fromDate && timeModel.toDatestampString(effectiveDate.fromDate),
      toDate: effectiveDate.toDate && timeModel.toDatestampString(effectiveDate.toDate)
    };
  },
  serialize(_ref) {
    let {
      fromDate,
      toDate
    } = _ref;
    return fromDate && toDate ? "".concat(timeModel.toDatestampString(fromDate), ",").concat(timeModel.toDatestampString(toDate)) : '';
  },
  deserialize(str) {
    const splitted = str.split(',');
    return splitted.length === 2 ? {
      fromDate: timeModel.fromDatestampString(splitted[0]),
      toDate: timeModel.fromDatestampString(splitted[1])
    } : {};
  }
};
const lastBillingDateConfig = {
  hasValue(value) {
    return value.type !== LastBillingDateType.None;
  },
  getEmptyValue() {
    return {
      type: LastBillingDateType.None
    };
  },
  getRequestParam(lastBillingDate) {
    const isLastBilledWithinRange = lastBillingDate.type === LastBillingDateType.Range;
    return {
      lastBilled: lastBillingDate.type !== LastBillingDateType.None ? lastBillingDate.type : undefined,
      lastBilledFrom: isLastBilledWithinRange ? timeModel.toDatestampString(lastBillingDate.fromDate) : undefined,
      lastBilledTo: isLastBilledWithinRange ? timeModel.toDatestampString(lastBillingDate.toDate) : undefined
    };
  },
  serialize(data) {
    if (data.type === LastBillingDateType.None) {
      return '';
    }
    if (data.type === LastBillingDateType.Range) {
      return "".concat(timeModel.toDatestampString(data.fromDate), ",").concat(timeModel.toDatestampString(data.toDate));
    }
    return data.type;
  },
  deserialize(lastBilled) {
    if (!lastBilled) {
      return {
        type: LastBillingDateType.None
      };
    }
    if (lastBilled === LastBillingDateType.Ever || lastBilled === LastBillingDateType.Never) {
      return {
        type: lastBilled
      };
    }
    const dates = lastBilled.split(',');
    if (dates.length !== 2) {
      return {
        type: LastBillingDateType.None
      };
    }
    const fromDate = timeModel.fromDatestampString(dates[0]);
    const toDate = timeModel.fromDatestampString(dates[1]);
    return {
      type: LastBillingDateType.Range,
      fromDate,
      toDate
    };
  }
};
export const filtersConfig = {
  [FilterType.EffectiveDate]: effectiveDateConfig,
  [FilterType.LastBillingDate]: lastBillingDateConfig,
  [FilterType.AgreementStatus]: getSimpleMultiSelectFilterConfig(FilterType.AgreementStatus),
  [FilterType.PaymentMethod]: getSimpleMultiSelectFilterConfig(FilterType.PaymentMethod),
  [FilterType.Completion]: getSimpleMultiSelectFilterConfig(FilterType.Completion),
  [FilterType.Recurrence]: getSimpleMultiSelectFilterConfig(FilterType.Recurrence),
  [FilterType.HasCredit]: getSimpleMultiSelectFilterConfig(FilterType.HasCredit),
  [FilterType.Automatic]: getSimpleMultiSelectFilterConfig(FilterType.Automatic),
  [FilterType.BillingTrigger]: getSimpleMultiSelectFilterConfig(FilterType.BillingTrigger),
  [FilterType.ServiceNames]: getSimpleMultiSelectFilterConfig(FilterType.ServiceNames),
  [FilterType.ServicePriceIncreaseState]: getSimpleMultiSelectFilterConfig(FilterType.ServicePriceIncreaseState),
  [FilterType.HasAmendments]: getSimpleMultiSelectFilterConfig(FilterType.HasAmendments)
};
export function serializeFilter(type, value) {
  const {
    serialize
  } = filtersConfig[type];
  return serialize ? serialize(value) : value;
}
export function makeTableFilterConfig(serviceNameOptions) {
  return {
    [FilterType.AgreementStatus]: {
      type: SearchFilterType.MultiSelectFilter,
      removable: true,
      options: agreementStatusFilterOptions
    },
    [FilterType.EffectiveDate]: {
      type: SearchFilterType.CustomFilter,
      removable: true,
      component: EffectiveDateFilter,
      initialValue: {}
    },
    [FilterType.LastBillingDate]: {
      type: SearchFilterType.CustomFilter,
      removable: true,
      component: LastBillingDateFilter,
      initialValue: {
        type: LastBillingDateType.None
      }
    },
    [FilterType.PaymentMethod]: {
      type: SearchFilterType.MultiSelectFilter,
      removable: true,
      options: paymentMethodFilterOptions
    },
    [FilterType.HasCredit]: {
      type: SearchFilterType.MultiSelectFilter,
      removable: true,
      options: hasCreditFilterOptions
    },
    [FilterType.Completion]: {
      type: SearchFilterType.MultiSelectFilter,
      removable: true,
      options: completionFilterOptions
    },
    [FilterType.Recurrence]: {
      type: SearchFilterType.MultiSelectFilter,
      removable: true,
      options: recurrenceFilterOptions
    },
    [FilterType.Automatic]: {
      type: SearchFilterType.MultiSelectFilter,
      removable: true,
      options: automaticFilterOptions
    },
    [FilterType.BillingTrigger]: {
      type: SearchFilterType.MultiSelectFilter,
      removable: true,
      options: billingTriggerFilterOptions
    },
    [FilterType.ServiceNames]: {
      type: SearchFilterType.MultiSelectFilter,
      removable: true,
      withSearchbox: true,
      options: serviceNameOptions
    },
    [FilterType.ServicePriceIncreaseState]: {
      type: SearchFilterType.MultiSelectFilter,
      removable: true,
      options: priceIncreaseFilterOptions
    },
    [FilterType.HasAmendments]: {
      type: SearchFilterType.MultiSelectFilter,
      removable: true,
      options: hasAmendmentsFilterOptions
    }
  };
}